import { useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { AlertCircle, CheckCircle2, Loader2 } from "lucide-react";
import { useLanguage } from "@/context/LanguageContext";
import { useCart } from "@/context/CartContext";
import { api, eur } from "@/lib/api";
import { trackPurchase } from "@/lib/analytics";
import Reveal from "@/components/Reveal";

export default function Success() {
  const { t } = useLanguage();
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const codId = params.get("cod");
  const { clear } = useCart();
  const [phase, setPhase] = useState(codId ? "cod" : "pending");
  const [order, setOrder] = useState(null);
  const attempts = useRef(0);

  useEffect(() => {
    if (codId) {
      clear();
      api.order(codId).then(trackPurchase).catch(() => {});
      return;
    }
    if (!sessionId) {
      setPhase("failed");
      return;
    }
    let timer;
    const tick = async () => {
      try {
        const res = await api.paymentStatus(sessionId);
        if (res.payment_status === "paid") {
          clear();
          setOrder(res.order);
          setPhase("paid");
          trackPurchase(res.order);
          return;
        }
      } catch {
        /* keep polling */
      }
      attempts.current += 1;
      if (attempts.current < 20) {
        timer = setTimeout(tick, 2000);
      } else {
        setPhase("failed");
      }
    };
    tick();
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionId]);

  if (phase === "pending") {
    return (
      <div data-testid="payment-pending" className="flex min-h-screen flex-col items-center justify-center gap-6 bg-surface pt-32">
        <Loader2 size={40} strokeWidth={1.25} className="animate-spin text-gold-dark" />
        <h1 className="font-display text-3xl font-light text-navy">{t.pay.pending}</h1>
        <p className="max-w-sm text-center text-sm font-light text-navy/50">{t.pay.pendingSub}</p>
      </div>
    );
  }

  if (phase === "failed") {
    return (
      <div data-testid="payment-failed" className="flex min-h-screen flex-col items-center justify-center gap-6 bg-surface pt-32">
        <AlertCircle size={40} strokeWidth={1.25} className="text-gold-dark" />
        <h1 className="font-display text-3xl font-light text-navy">{t.common.error}</h1>
        <Link to="/checkout" data-testid="failed-retry-cta" className="btn-primary-luxe">{t.pay.retry}</Link>
      </div>
    );
  }

  if (phase === "cod") {
    return (
      <div data-testid="success-page" className="flex min-h-screen items-center justify-center bg-surface pt-32">
        <div className="mx-auto max-w-lg px-6 pb-24 text-center">
          <Reveal>
            <CheckCircle2 size={48} strokeWidth={1} className="mx-auto text-gold-dark" />
            <h1 className="mt-8 font-display text-4xl font-light tracking-tight text-navy sm:text-5xl">{t.success.codTitle || t.success.title}</h1>
          </Reveal>
          <Reveal delay={1}>
            <p className="mt-5 text-sm font-light leading-relaxed text-navy/55">{t.success.codSub || t.success.sub}</p>
          </Reveal>
          <Reveal delay={2}>
            <div data-testid="success-order-details" className="mt-10 border border-navy/10 bg-white p-8 text-start">
              <div className="flex justify-between text-sm">
                <span className="text-navy/50">{t.success.order}</span>
                <span data-testid="success-order-number" className="font-semibold text-gold-dark">{codId}</span>
              </div>
            </div>
          </Reveal>
          <Reveal delay={3} className="mt-10">
            <Link to="/" data-testid="success-home-cta" className="btn-primary-luxe">{t.success.cta}</Link>
          </Reveal>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="success-page" className="flex min-h-screen items-center justify-center bg-surface pt-32">
      <div className="mx-auto max-w-lg px-6 pb-24 text-center">
        <Reveal>
          <CheckCircle2 size={48} strokeWidth={1} className="mx-auto text-gold-dark" />
          <h1 className="mt-8 font-display text-4xl font-light tracking-tight text-navy sm:text-5xl">{t.success.title}</h1>
        </Reveal>
        <Reveal delay={1}>
          <p className="mt-5 text-sm font-light leading-relaxed text-navy/55">{t.success.sub}</p>
        </Reveal>
        {order && (
          <Reveal delay={2}>
            <div data-testid="success-order-details" className="mt-10 border border-navy/10 bg-white p-8 text-start">
              <div className="flex justify-between text-sm">
                <span className="text-navy/50">{t.success.order}</span>
                <span data-testid="success-order-number" className="font-semibold text-gold-dark">{order.id}</span>
              </div>
              <div className="mt-4 space-y-2 border-t border-navy/10 pt-4">
                {order.items.map((i) => (
                  <div key={i.slug} className="flex justify-between text-xs text-navy/60">
                    <span>{i.name} × {i.qty}</span>
                    <span>{eur(i.line_total)}</span>
                  </div>
                ))}
              </div>
              <div className="mt-4 flex justify-between border-t border-navy/10 pt-4 text-sm font-semibold text-navy">
                <span>{t.cart.total}</span>
                <span data-testid="success-order-total">{eur(order.total)}</span>
              </div>
            </div>
          </Reveal>
        )}
        <Reveal delay={3} className="mt-10">
          <Link to="/" data-testid="success-home-cta" className="btn-primary-luxe">{t.success.cta}</Link>
        </Reveal>
      </div>
    </div>
  );
}
